import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { DsBadgeTheme } from '@design-system/ds-badge/types/ds-badge-theme.type';

@Component({
  selector: 'app-ds-badge',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './ds-badge.component.html',
  styleUrls: ['./ds-badge.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsBadgeComponent {
  /**
   * Text label displayed on the badge
   */
  @Input() label: string | null = null;

  /**
   * Controls the size of the badge. Sizing is described in Figma Design System
   *
   * @see https://www.figma.com/design/XWMrlpfWAaF8xB2CpN8Zk3/%F0%9F%93%98-Strata-Design-System?node-id=13045-10179&m=dev
   * @default 'md'
   */
  @Input() size: 'sm' | 'md' | 'lg' = 'md';

  /**
   * Controls the visual theme/style of the badge. Styling is described in Figma Design System
   *
   * @see https://www.figma.com/design/XWMrlpfWAaF8xB2CpN8Zk3/%F0%9F%93%98-Strata-Design-System?node-id=13050-12247&p=f&m=dev
   * @default 'primary'
   */
  @Input() theme: DsBadgeTheme = 'primary';

  /**
   * Name of the icon to display inside the badge. (Material Symbols)
   *
   * @see https://fonts.google.com/icons
   */
  @Input() icon = '';

  /**
   * Font size of the icon (in px)
   *
   * @default 12px
   */
  @Input() iconSize = '12px';

  /**
   * The position of the icon inside the badge
   *
   * @default 'before'
   */
  @Input() iconPosition: 'before' | 'after' = 'before';

  /**
   * Icon font set to use for icon.
   *
   * @default 'material-symbols-rounded'
   */
  @Input() iconFontSet: 'material-icons' | 'material-symbols-outlined' | 'material-symbols-rounded' | 'material-symbols-sharp' =
    'material-symbols-rounded';

  /**
   * Returns a list of CSS classes for the badge based on its current state.
   *
   * Classes are based on:
   * - badge size
   * - badge theme
   * - badge icon position (if present)
   */
  get badgeClasses(): string[] {
    return [
      'ds-badge--' + this.size,
      'ds-badge--' + this.theme,
      this.icon ? `ds-badge--icon-${this.iconPosition}` : '',
      this.icon && !this.label ? 'ds-badge--icon-only' : '',
    ];
  }
}
